import React, { useRef, useState, useEffect, forwardRef } from 'react';
import { Box, IconButton, Tooltip, LinearProgress, Typography, SxProps, Theme } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReactDOM from 'react-dom';

interface CustomAudioPlayerProps {
  src: string;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  tooltipTitle?: string;
  showTimeline?: boolean;
  sx?: SxProps<Theme>;
}

const formatTime = (seconds: number) => {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${m}:${s}`;
};

const CustomAudioPlayer = forwardRef<HTMLDivElement, CustomAudioPlayerProps>(
  ({ src, isPlaying, onPlay, onPause, tooltipTitle, showTimeline = true, sx, ...props }, ref) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [muted, setMuted] = useState(false);
    const moreButtonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    useEffect(() => {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      }
    }, [isPlaying]);

    useEffect(() => {
      if (dropdownOpen) {
        const handleClickOutside = (event: MouseEvent) => {
          if (
            moreButtonRef.current &&
            !moreButtonRef.current.contains(event.target as Node) &&
            !(dropdownRef.current && dropdownRef.current.contains(event.target as Node))
          ) {
            setDropdownOpen(false);
          }
        };
        window.addEventListener('mousedown', handleClickOutside);

        // Custom event to close all dropdowns
        const handleCloseAll = () => setDropdownOpen(false);
        window.addEventListener('closeAllAudioDropdowns', handleCloseAll);

        return () => {
          window.removeEventListener('mousedown', handleClickOutside);
          window.removeEventListener('closeAllAudioDropdowns', handleCloseAll);
        };
      }
    }, [dropdownOpen]);

    const handlePlayPause = () => {
      if (isPlaying) {
        onPause();
      } else {
        onPlay();
      }
    };

    const handleTimeUpdate = () => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
        setProgress((audioRef.current.currentTime / (audioRef.current.duration || 1)) * 100);
      }
    };

    const handleLoadedMetadata = () => {
      if (audioRef.current) {
        setDuration(audioRef.current.duration);
      }
    };

    const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (!audioRef.current) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const newTime = (clickX / rect.width) * duration;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    };

    const handleMoreClick = () => {
      window.dispatchEvent(new Event('closeAllAudioDropdowns'));
      if (moreButtonRef.current) {
        const rect = moreButtonRef.current.getBoundingClientRect();
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + 4,
          left: rect.left,
          zIndex: 9999,
          minWidth: rect.width,
          background: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          borderRadius: 8,
          padding: '4px 0',
        });
        setDropdownOpen(true);
      }
    };

    const handleMenuClose = () => setDropdownOpen(false);

    const handleMuteToggle = () => {
      if (audioRef.current) {
        audioRef.current.muted = !audioRef.current.muted;
        setMuted(audioRef.current.muted);
      }
      handleMenuClose();
    };

    const handleDownload = () => {
      const link = document.createElement('a');
      link.href = src;
      link.download = src.split('/').pop() || 'audio';
      link.click();
      handleMenuClose();
    };

    return (
      <Box 
        ref={ref} 
        {...props}
        sx={{
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          minWidth: showTimeline ? 200 : 0,
          ...(sx as any)
        }}
      >
        <IconButton
          onClick={handlePlayPause}
          sx={{
            backgroundColor: '#FF7043',
            color: '#fff',
            '&:hover': { backgroundColor: '#F4511E' },
            width: 32,
            height: 32,
          }}
        >
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        {showTimeline && (
          <>
            <Box
              sx={{ flex: 1, mx: 1, cursor: 'pointer' }}
              onClick={handleProgressBarClick}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 6, borderRadius: 3, background: '#eee' }}
              />
            </Box>
            <Typography sx={{ fontSize: 12, minWidth: 48, textAlign: 'right' }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </>
        )}
        <IconButton ref={moreButtonRef} onClick={handleMoreClick}>
          <MoreVertIcon />
        </IconButton>
        {dropdownOpen && typeof window !== 'undefined' && ReactDOM.createPortal(
          <Box ref={dropdownRef} style={dropdownStyle}>
            <Box
              sx={{
                fontSize: '14px',
                padding: '8px 16px',
                cursor: 'pointer',
                '&:hover': { background: '#f5f5f5' }
              }}
              onClick={handleMuteToggle}
            >
              {muted ? 'Unmute' : 'Mute'}
            </Box>
            <Box
              sx={{
                fontSize: '14px',
                padding: '8px 16px',
                cursor: 'pointer',
                '&:hover': { background: '#f5f5f5' }
              }}
              onClick={handleDownload}
            >
              Download
            </Box>
          </Box>,
          document.body
        )}
        <audio
          ref={audioRef}
          src={src}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={onPause}
          style={{ display: 'none' }}
        />
      </Box>
    );
  }
);

CustomAudioPlayer.displayName = 'CustomAudioPlayer';

export default CustomAudioPlayer; 